import {NavbarStateResourceNavbarDisplayModeEnum} from '@navbar-api';
import {useAvailabilityStore} from '@navbar-logged/features/availability/availability.store';
import {useLanguageSelectorStore} from '@navbar-logged/features/language-selector';
import {useNotificationsStore} from '@navbar-logged/features/notifications';
import {useProfilePreferencesStore, useUserStore} from '@navbar-logged/features/user';
import {callWithNuxt} from 'nuxt/app';
import {createFeatureFlagsStore, useNuxtApp} from '#imports';
import {FreelanceContext, FreelanceProfilePreferencesContext, NavbarContext} from './apis.registry';
import {useNavbarStateStore} from './navbar-state.store';

export const useFeatureFlagsStoreLogged = createFeatureFlagsStore(
    ['platform-shutdown-alert', 'purchase-order-monitoring-in-project-client'],
    'navbar-feature-flags',
);

export function useAppContext() {
    const nuxtApp = useNuxtApp();
    const pinia = nuxtApp.$pinia;

    const navbarState = useNavbarStateStore(pinia);
    const userStore = useUserStore(pinia);
    const availabilityStore = useAvailabilityStore(pinia);
    const profilePreferencesStore = useProfilePreferencesStore(pinia);
    const notificationsStore = useNotificationsStore(pinia);
    const languageSelector = useLanguageSelectorStore(pinia);
    const featureFlagsStoreLogged = useFeatureFlagsStoreLogged(pinia);

    async function loadNavbarState() {
        const context = await NavbarContext.getContext();
        if (context.state && navbarState.state.navbarDisplayMode === NavbarStateResourceNavbarDisplayModeEnum.Funnel) {
            context.state.navbarDisplayMode = NavbarStateResourceNavbarDisplayModeEnum.Funnel;
        }
        if (context) {
            navbarState.$patch(context);
        }
    }

    async function loadUser() {
        const user = await NavbarContext.getUser();
        userStore.$patch({user});
    }

    /** Load availability and preferences */
    async function loadAvailability() {
        if (userStore.hasSelectedFreelanceIdentity) {
            const [availability, preferences] = await Promise.allSettled([
                FreelanceContext.getAvailability(),
                FreelanceProfilePreferencesContext.getProfilePreferences(),
            ]);
            if (availability.status === 'fulfilled') {
                availabilityStore.availability = availability.value;
            }
            if (preferences.status === 'fulfilled') {
                profilePreferencesStore.preferences = preferences.value;
            }
        }
    }

    async function loadNotifications() {
        await notificationsStore.updateNotifications();
        if (import.meta.client) {
            notificationsStore.startUpdating();
        }
    }

    async function loadAppContext() {
        async function loadAllStates() {
            await Promise.allSettled([
                featureFlagsStoreLogged.loadFeatureFlags(),
                loadNavbarState(),
                loadUser(),
                languageSelector.fetchAllLanguages(),
            ]);
            await callWithNuxt(nuxtApp, loadNotifications);
            await callWithNuxt(nuxtApp, () => languageSelector.redirectIfLgQueryPresent());
            await callWithNuxt(nuxtApp, loadAvailability);
        }

        try {
            navbarState.pending = true;
            await loadAllStates();
        } catch (e) {
            /* eslint-disable */
            console.error(`Navbar loader error:`, e);
            return null;
        } finally {
            navbarState.pending = false;
        }
    }

    return {
        loadNotifications,
        loadAppContext,
    };
}
